// Ops · Refunds: the queue, marking one done (provider ref) or failed, and refunding an order
// (docs/PHASE7-CONTRACT.md, contract.d.ts "Ops · Refunds"). Pure: node --test runs it.
import { refundProblem } from '@popcart/core/v1';
import { peso } from '../../lib/format.js';
import { parsePesos } from '../express/lib.js';

/** @typedef {import('@popcart/core/v1').OpsRefund} OpsRefund */
/** @typedef {import('@popcart/core/v1').OpsBoardColumn} OpsBoardColumn */
/** @typedef {import('@popcart/core/v1').PopCartApi} PopCartApi */
/** @typedef {'pending' | 'done' | 'failed' | 'all'} RefundFilter */

/** @param {RefundFilter} status */
export const refundsKey = (status) => ['ops', 'refunds', status];

/** @type {Record<OpsRefund['status'], { label: string; tone: 'warning' | 'success' | 'error' }>} */
export const REFUND_PILL = {
  pending: { label: 'To send', tone: 'warning' },
  done: { label: 'Done', tone: 'success' },
  failed: { label: 'Failed', tone: 'error' },
};

/** Pending first (oldest first: send money back in order), then the rest newest first. @param {readonly OpsRefund[]} rows */
export function sortRefunds(rows) {
  const t = (/** @type {string} */ s) => Date.parse(s) || 0;
  return [...rows].sort((a, b) => {
    const pa = a.status === 'pending' ? 0 : 1;
    const pb = b.status === 'pending' ? 0 : 1;
    if (pa !== pb) return pa - pb;
    return pa === 0 ? t(a.createdAt) - t(b.createdAt) : t(b.createdAt) - t(a.createdAt);
  });
}

/** "Catch-weight" for settlement refunds, "Manual" otherwise. @param {OpsRefund} r */
export function sourceLabel(r) {
  return r.source === 'settlement' ? 'Catch-weight' : 'Manual';
}

export const PROVIDER_REF_MAX = 120;
export const REFUND_REASON = { min: 3, max: 200 };

/**
 * Mark a pending refund done (needs the PayMongo refund id or bank reference) or failed.
 * @param {{ status: 'done' | 'failed'; providerRef: string; note: string }} form
 * @returns {{ ok: true; value: { status: 'done' | 'failed'; providerRef?: string; note?: string } } | { ok: false; errors: { providerRef?: string; note?: string } }}
 */
export function validateProcess(form) {
  const ref = String(form.providerRef ?? '').trim();
  const note = String(form.note ?? '').trim();
  /** @type {{ providerRef?: string; note?: string }} */
  const errors = {};
  if (form.status === 'done' && !ref) errors.providerRef = 'Add the refund reference from PayMongo or the bank.';
  else if (ref.length > PROVIDER_REF_MAX) errors.providerRef = `Keep it under ${PROVIDER_REF_MAX} characters.`;
  if (form.status === 'failed' && note.length < 3) errors.note = 'Say what went wrong, for whoever tries next.';
  else if (note.length > REFUND_REASON.max) errors.note = `Keep it under ${REFUND_REASON.max} characters.`;
  if (errors.providerRef || errors.note) return { ok: false, errors };
  /** @type {{ status: 'done' | 'failed'; providerRef?: string; note?: string }} */
  const value = { status: form.status };
  if (ref) value.providerRef = ref;
  if (note) value.note = note;
  return { ok: true, value };
}

/**
 * @param {Pick<PopCartApi, 'processRefund'>} api
 * @param {string} adjustmentId
 * @param {{ status: 'done' | 'failed'; providerRef: string; note: string }} form
 */
export async function processRefundAction(api, adjustmentId, form) {
  const r = validateProcess(form);
  if (!r.ok) return r;
  const refund = await api.processRefund(adjustmentId, r.value);
  return { ok: /** @type {const} */ (true), refund };
}

/** "pc-1042", " PC 1042 " and "1042" all mean PC-1042. @param {string} code */
export function normaliseCode(code) {
  const s = String(code ?? '').trim().toUpperCase().replace(/\s+/g, '-');
  if (!s) return '';
  if (/^\d+$/.test(s)) return `PC-${s}`;
  return s.replace(/^PC-?/, 'PC-');
}

/**
 * Find an order by its code in what Ops already has: the live board (today's orders, every
 * stage) and the refunds list (orders refunded before, with what's still refundable).
 * There is no order-code lookup route (reported as a contract gap); Customers → order → Refund
 * covers older orders by passing the order id.
 * @param {string} code
 * @param {{ board?: OpsBoardColumn[]; refunds?: readonly OpsRefund[] }} sources
 * @returns {{ orderId: string; code: string; refundableCentavos: number | null; payment: string | null } | null}
 */
export function findOrder(code, { board, refunds }) {
  const want = normaliseCode(code);
  if (!want) return null;
  const prior = (refunds ?? []).filter((r) => r.code.toUpperCase() === want).sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))[0];
  for (const col of board ?? []) {
    const o = col.orders.find((x) => x.code.toUpperCase() === want);
    if (o) return { orderId: o.orderId, code: o.code, refundableCentavos: prior ? prior.refundableCentavos : null, payment: o.payment };
  }
  if (prior) return { orderId: prior.orderId, code: prior.code, refundableCentavos: prior.refundableCentavos, payment: prior.paymentMethod };
  return null;
}

/**
 * The "Refund an order" form. The amount must be more than zero and, when we know it, no more
 * than what's still refundable (money.refundProblem); the API checks again.
 * @param {{ amount: string; reason: string }} form
 * @param {number | null} refundableCentavos
 * @returns {{ ok: true; value: { amountCentavos: number; reason: string } } | { ok: false; errors: { amount?: string; reason?: string } }}
 */
export function validateRefund(form, refundableCentavos) {
  /** @type {{ amount?: string; reason?: string }} */
  const errors = {};
  const raw = String(form.amount ?? '').trim();
  const c = parsePesos(raw);
  if (!raw) errors.amount = 'Enter the amount to send back, in pesos.';
  else if (c === null) errors.amount = 'Pesos and centavos only, like 120 or 119.50';
  else {
    const p = refundProblem(c, refundableCentavos ?? Number.MAX_SAFE_INTEGER);
    if (p === 'amount_invalid') errors.amount = 'More than ₱0.';
    else if (p === 'over_refundable') errors.amount = `Up to ${peso(refundableCentavos)} can still go back on this order.`;
  }
  const reason = String(form.reason ?? '').trim();
  if (reason.length < REFUND_REASON.min) errors.reason = 'Say why, like “Missing item” (the customer sees it).';
  else if (reason.length > REFUND_REASON.max) errors.reason = `Keep it under ${REFUND_REASON.max} characters.`;
  if (errors.amount || errors.reason) return { ok: false, errors };
  return { ok: true, value: { amountCentavos: /** @type {number} */ (c), reason } };
}

/**
 * @param {Pick<PopCartApi, 'createRefund'>} api
 * @param {string} orderId
 * @param {{ amount: string; reason: string }} form
 * @param {number | null} refundableCentavos
 */
export async function createRefundAction(api, orderId, form, refundableCentavos) {
  const r = validateRefund(form, refundableCentavos);
  if (!r.ok) return r;
  const refund = await api.createRefund(orderId, r.value);
  return { ok: /** @type {const} */ (true), refund };
}
